import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, Input, OnInit, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSidenav } from '@angular/material/sidenav';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PermissionsHelper } from '../../../app/helpers/permissions.helper';
import { Affiliate } from '../../../app/models/affiliate.model';
import { MpsUser } from '../../../app/models/mps-user.model';
import { AffiliatesService } from '../../../app/services/affiliates.service';
import { mpsRouteNames } from '../../mps-route-names';
import { MpsUserService } from '../../services/mps-user.service';
import { MpsNavigationComponent } from '../mps-navigation/mps-navigation.component';

@Component({
  selector: 'mps-app-header',
  templateUrl: './mps-header.component.html',
  styleUrls: ['./mps-header.component.scss'],
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MpsNavigationComponent,
    RouterModule,
    TranslateModule,
  ],
})
export class MpsHeaderComponent implements OnInit {
  @Input() public nav?: MatSidenav;
  translateService = inject(TranslateService);
  private readonly affiliatesService = inject(AffiliatesService);
  private readonly router = inject(Router);
  private readonly userService = inject(MpsUserService);
  private readonly _eRef = inject(ElementRef);
  user = signal(this.userService.getUserSubject().getValue());
  isDropdownOpen = signal(false);
  logoutRoute = mpsRouteNames.auth + '/' + mpsRouteNames.logout;

  affiliates: Affiliate[] = [];

  ngOnInit(): void {
    this.initData();
  }

  logOut(): void {
    this.user.set(null);
  }

  isUserLoggedIn(): boolean {
    return this.user() !== null;
  }

  getCurrentLangLabel(): string {
    return this.getLangLabel(this.translateService.currentLang);
  }

  getLangLabel(lang: string): string {
    const labels: Record<string, string> = {
      fr: 'Français',
      en: 'English',
    };
    return labels[lang] || this.translateService.instant('general.missing_translation');
  }

  toggleDropdown() {
    this.isDropdownOpen.set(!this.isDropdownOpen());
  }

  // TODO: No switching language for now, uncomment when needed
  // switchLanguage(language: string) {
  //   this.translateService.use(language);
  //   const user = this.userService.getUser();
  //   localStorage.setItem('lang', language);
  //   if (user) {
  //     this.userService.changeLang(user.id, language)?.subscribe();
  //   }
  // }

  getUserDisplayName() {
    return this.user()?.firstname + ' ' + this.user()?.lastname.substring(0, 1) + '.';
  }

  getCurrentAffiliateName(): string {
    return this.user()?.getAffiliateName() ?? '';
  }

  getCurrentRole(): string {
    const role = this.user()?.role;
    if (role) {
      return this.translateService.instant(`entities.roles.${role.code.toLowerCase()}`);
    }

    return '';
  }

  getDefaultLocationName() {
    return this.user()?.getDefaultLocationName() ?? '';
  }

  isCurrentAffiliate(affiliateId: number): boolean {
    return this.user()?.affiliate_id === affiliateId;
  }

  canChangeAffiliate(): boolean {
    if (!this.user()) {
      return false;
    }

    return PermissionsHelper.canChangeAffiliate(this.user()!);
  }

  changeAffiliate(affiliateId: number): void {
    this.userService.changeAffiliate(affiliateId).subscribe({
      next: (user: MpsUser) => {
        this.user.set(user);
        window.location.reload();
      },
      error: () => {
        // Nothing to do
      },
    });
  }

  private initData(): void {
    this.affiliatesService.getAll().subscribe({
      next: (affiliates: Affiliate[]) => {
        this.affiliates = affiliates;
      },
      error: () => {
        this.affiliates = [];
      },
    });
  }

  @HostListener('document:click', ['$event'])
  handleClickOutside(event: Event) {
    if (!this._eRef.nativeElement.contains(event.target)) {
      this.isDropdownOpen.set(false);
    }
  }
}
